/**
 * Deck Context and Provider
 *
 * This module provides React Context for managing deck state throughout the Auriga application.
 * It handles fetching available decks, tracking the current deck, and automatically updating
 * the current deck based on route parameters.
 *
 * Key responsibilities:
 * - Fetch and manage available decks
 * - Track and update the current deck based on route changes
 * - Provide error handling for deck-related operations
 * - Integrate with React Query for efficient data fetching and caching
 */

import { useRootNodesForDimensions } from '@amzn/gft-dimension-picker';
import { KPI_SUPPORTED_DIMSUM_DIMENSIONS } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { useUserContext } from '@context/UserContext';
import { setLockedTimestampProvider } from '@services/api';
import { Deck } from '@services/api/deckManagement/graphql/schema/API';
import { useAllDecks } from '@services/api/deckManagement/hooks/useAllDecks';
import { useDeck } from '@services/api/deckManagement/hooks/useDeck';
import { useDeckSubscription } from '@services/api/deckManagement/hooks/useDeckSubscription';
import { usePageLockSubscription } from '@services/api/deckManagement/hooks/usePageLockSubscription';
import { useAuth } from '@utils/auth/AuthProvider';
import { DECK_PK_PREFIX, entityIdAsKey } from '@utils/deckHelpers';
import { isEmpty } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeckContextType, DeckProviderProps, LockStatus } from 'types/DeckContext.types';

/**
 * Deck Context
 *
 * React context for deck state management.
 */
export const DeckContext = createContext<DeckContextType>({
  availableDecks: [],
  currentDeck: null,
  currentDeckId: null,
  setCurrentDeck: () => {},
  isLoading: false,
  isError: false,
  error: null,
  rootNodes: {},
  isRootNodesError: false,
  isRootNodesLoading: false,
  rootNodesError: null,
  deckLockStatus: { isLocked: false },
  setDeckLockStatus: () => {},
  lockAlertId: null,
  setLockAlertId: () => {},
});

/**
 * Deck Provider Component
 *
 * This component provides deck context to the application.
 * It manages the state of available decks and the current deck selection.
 *
 * Key responsibilities:
 * - Fetch and manage available decks using React Query
 * - Automatically detect and set current deck from route parameters
 * - Handle fallback fetching for individual decks not in available decks
 * - Provide error handling and loading states
 * - Track deck selection changes and update context accordingly
 *
 * @param {DeckProviderProps} props - The component props
 * @returns {JSX.Element} The provider component
 */
export const DeckContextProvider = ({ children }: DeckProviderProps): ReactNode => {
  // Get deckId from route parameters
  const { deckId } = useParams<{ deckId: string }>();
  const { clearAllAlerts } = useAurigaContext();
  const { username } = useUserContext();
  const { isAuthenticated } = useAuth();

  // State for current deck and error handling
  const [currentDeck, setCurrentDeckState] = useState<Deck | null>(null);
  const [currentDeckId, setCurrentDeckId] = useState<string | null>(null);
  const [deckError, setDeckError] = useState<string | null>(null);
  const [availableDecks, setAllAvailableDecks] = useState<Deck[]>([]);
  const [rootNodesState, setRootNodesState] = useState<ReturnType<typeof useRootNodesForDimensions>['data']>({});
  const [deckLockStatus, setDeckLockStatus] = useState<LockStatus>({ isLocked: false });
  const lockAlertId = useRef<string | null>(null);
  const setLockAlertId = useCallback((alertId: string | null) => {
    lockAlertId.current = alertId;
  }, []);

  // Initialize and update deckLockStatus when currentDeck changes
  useEffect(() => {
    if (currentDeck) {
      const lockedBy = currentDeck.locked_by ?? undefined;
      const lockedAt = currentDeck.locked_at ?? undefined;
      // lock_expiry is the authoritative absolute expiry (ISO-8601).
      const lockExpiry = currentDeck.lock_expiry ?? undefined;
      const isLocked = !!lockedBy;
      const isLockedByCurrentUser = isLocked && lockedBy === username;
      setDeckLockStatus({
        isLocked,
        lockedBy,
        lockedAt,
        lockExpiry,
        isLockedByCurrentUser,
        isEditingDisabled: !isLocked || (isLocked && !isLockedByCurrentUser),
      });
    } else {
      // Reset lock status when no current deck
      setDeckLockStatus({ isLocked: false });
    }
  }, [currentDeck, username]);

  // Fetch all available decks
  const { data: decks, isLoading: isDecksLoading, isError: isDecksError, error: decksError } = useAllDecks();

  // Fetch individual deck when needed (fallback for decks not in availableDecks)
  const {
    data: individualDeck,
    isLoading: isIndividualDeckLoading,
    isError: isIndividualDeckError,
    error: individualDeckError,
  } = useDeck({
    deckId,
  });

  // Subscribe to real-time Deck-related events
  useDeckSubscription({
    deckId: deckId || '',
    enabled: !isEmpty(deckId) && !isEmpty(username) && isAuthenticated,
  });

  // Subscribe to real-time page-lock events
  usePageLockSubscription({
    deckId: deckId || '',
    enabled: !isEmpty(deckId) && !isEmpty(username) && isAuthenticated,
  });

  useEffect(() => {
    if (decks) {
      setAllAvailableDecks(decks);
    }
  }, [decks]);

  /**
   * Set current deck by ID
   *
   * Attempts to find and set the current deck by its ID. First searches
   * in the availableDecks array, then falls back to individual deck fetching
   * if not found.
   *
   * This is the primary way to change which deck is being viewed/edited.
   * This function is idempotent - calling it multiple times with the same deckId
   * will not trigger additional API calls or unnecessary re-renders.
   *
   * NOTE: This gets called automatically when the `:deckId` Route/Path Param is updated.
   *
   * @param {string} deckId - The ID of the deck to set as current
   */
  const setCurrentDeck = useCallback(
    (targetDeckId: string) => {
      setDeckError(null);

      // Clear alerts only when switching to a different deck
      if (targetDeckId !== currentDeckId) {
        clearAllAlerts();
      }

      // Always proceed to ensure fresh data (since multi-user lock status might have changed)
      // First, try to find the deck in availableDecks
      const foundDeck = availableDecks.find((deck) => deck.pk === entityIdAsKey(targetDeckId, DECK_PK_PREFIX));

      if (foundDeck) {
        setCurrentDeckState(foundDeck);
        setCurrentDeckId(targetDeckId);
      } else if (individualDeck && individualDeck.pk === entityIdAsKey(targetDeckId, DECK_PK_PREFIX)) {
        // Use individually fetched deck if it matches the target
        setCurrentDeckState(individualDeck);
        setCurrentDeckId(targetDeckId);
      } else if (!availableDecks.length && !isDecksLoading) {
        // If no decks are available, and we're not loading, set error
        setDeckError(`Deck with ID "${targetDeckId}" could not be found.`);
        setCurrentDeckState(null);
        setCurrentDeckId(null);
      }
      // If availableDecks is still loading, we wait for it to complete
      // The useEffect below will handle setting the deck once data is available
    },
    [availableDecks, currentDeckId, individualDeck, isDecksLoading, clearAllAlerts],
  );

  /**
   * Effect to automatically set current deck based on route parameter
   *
   * This effect runs when the deckId route parameter changes and attempts
   * to set the current deck accordingly.
   */
  useEffect(() => {
    if (deckId) {
      setCurrentDeck(deckId);
    } else {
      // Clear current deck if no deckId in route
      setCurrentDeckState(null);
      setCurrentDeckId(null);
      setDeckError(null);
    }
  }, [deckId, setCurrentDeck]);

  /**
   * Effect to handle individual deck fetch errors
   *
   * When the individual deck fetch fails, we set an appropriate error message.
   */
  useEffect(() => {
    if (isIndividualDeckError && deckId) {
      setDeckError(`Deck with ID "${deckId}" could not be found.`);
      setCurrentDeckState(null);
      setCurrentDeckId(null);
    }
  }, [isIndividualDeckError, deckId, individualDeckError]);

  /**
   * Effect to clear deck error when decks are successfully loaded
   *
   * This helps reset error states when data becomes available.
   */
  useEffect(() => {
    if (availableDecks.length > 0 && deckError && deckId) {
      // Try to set the current deck again now that we have available decks
      const foundDeck = availableDecks.find((deck) => deck.pk === entityIdAsKey(deckId, DECK_PK_PREFIX));
      if (foundDeck) {
        setCurrentDeckState(foundDeck);
        setCurrentDeckId(deckId);
        setDeckError(null);
      }
    }
  }, [availableDecks, deckError, deckId]);

  // Combine loading states
  const isLoading = isDecksLoading || isIndividualDeckLoading;

  // Combine error states
  const isError = isDecksError || isIndividualDeckError || !!deckError;
  const error = deckError || (isDecksError ? decksError : null) || (isIndividualDeckError ? individualDeckError : null);

  // Root Nodes Fetching
  const {
    data: rootNodes,
    isLoading: isRootNodesLoading,
    isError: isRootNodesError,
    error: rootNodesError,
  } = useRootNodesForDimensions(KPI_SUPPORTED_DIMSUM_DIMENSIONS);

  /**
   * Sets the local root nodes data once Root Nodes are fetched.
   */
  useEffect(() => {
    if (!isLoading && rootNodes && Object.keys(rootNodes).length > 0) {
      setRootNodesState((prev) => {
        // Only update if data actually changed
        if (JSON.stringify(prev) !== JSON.stringify(rootNodes)) {
          return rootNodes;
        }
        return prev;
      });
    }
  }, [isLoading, rootNodes]);

  /**
   * Exposed Lock Timestamp for custom header addition, when available
   */
  useEffect(() => {
    setLockedTimestampProvider(() => deckLockStatus.lockedAt);
  }, [deckLockStatus.lockedAt]);

  /**
   * Create the context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  const contextValue = useMemo<DeckContextType>(
    () => ({
      availableDecks,
      currentDeck,
      currentDeckId,
      setCurrentDeck,
      isLoading,
      isError,
      error: error as Error | null,
      rootNodes: rootNodesState,
      isRootNodesLoading,
      isRootNodesError,
      rootNodesError: rootNodesError as Error | null,
      deckLockStatus,
      setDeckLockStatus,
      lockAlertId: lockAlertId.current,
      setLockAlertId,
    }),
    [
      availableDecks,
      currentDeck,
      currentDeckId,
      setCurrentDeck,
      isLoading,
      isError,
      error,
      rootNodesState,
      isRootNodesLoading,
      isRootNodesError,
      rootNodesError,
      deckLockStatus,
      setLockAlertId,
    ],
  );

  return <DeckContext value={contextValue}>{children}</DeckContext>;
};

/**
 * Custom hook to access the deck context
 *
 * @returns {DeckContextType} The deck context value
 */
export const useDeckContext = (): DeckContextType => {
  return use(DeckContext);
};

export default DeckContext;
